import React from 'react';
import debounce from 'lodash.debounce';
import { Moment } from 'moment';
import { Form, Row, Col, DatePicker, Select, Switch } from 'antd';
import clsx from 'clsx';
import { FormComponentProps } from 'antd/lib/form';
import UserInfo from 'src/types/UserInfo';
import { objectKeys } from 'src/utils/objectKeys';
import { objectFromEntries } from 'src/utils/objectFromEntries';
import UserContext from 'src/services/UserContext';
import { FiltersPayload } from '../ProductAlertsProvider';
import styles from './ProductSegmentFilters.module.less';

export type FiltersFormValues = {
  showAll: boolean | null;
  createdBy: string | null;
  favourite: boolean | null;
  createdStart: Moment | null;
  createdEnd: Moment | null;
};

export type FiltersFormProps = FormComponentProps & {
  user: UserInfo | null;
  segmentUsers: string[];
  onChange: (changes: Partial<FiltersFormValues>) => void;
  changeFilter: (payload: FiltersPayload) => void;
};

const { Option } = Select;

const SHOW_ALL = 'SHOW_ALL';

const convertMomentToString = (m: Moment): string => m.format('YYYY-MM-DD');

const getChangedFormValues = (allValues: FiltersFormValues): Partial<FiltersFormValues> => {
  return objectFromEntries(
    objectKeys(allValues)
      .filter((key) => allValues[key])
      .map((key) => [key, allValues[key]])
  );
};

const handleFormValuesChange = (
  { user, onChange, changeFilter }: FiltersFormProps,
  _: unused,
  allValues: FiltersFormValues
) => {
  const isCurrentUserSelected = allValues.createdBy === user?.userName;
  const changedFormValues = { ...allValues, ...(isCurrentUserSelected && { createdBy: null }) };
  onChange(getChangedFormValues(changedFormValues));
  changeFilter({
    showAll: isCurrentUserSelected ? null : true,
    favourite: allValues.favourite || null,
    createdBy: allValues.createdBy === SHOW_ALL ? null : allValues.createdBy,
    createdStart: allValues.createdStart ? convertMomentToString(allValues.createdStart) : null,
    createdEnd: allValues.createdEnd ? convertMomentToString(allValues.createdEnd) : null,
  });
};

const FiltersForm: React.FC<FiltersFormProps> = ({ form, user, segmentUsers }) => {
  const { getFieldDecorator, getFieldValue } = form;
  // TODO add proper TS typings for form fields since currently no types at all

  const disabledStartDate = (startValue: Moment | null) => {
    const endValue = getFieldValue('createdEnd');
    if (!startValue || !endValue) {
      return false;
    }
    return startValue.valueOf() > endValue.valueOf();
  };

  const disabledEndDate = (endValue: Moment | null) => {
    const startValue = getFieldValue('createdStart');
    if (!endValue || !startValue) {
      return false;
    }
    return endValue.valueOf() <= startValue.valueOf();
  };

  return (
    <Form className={clsx(styles.form, 'widget_color')}>
      <label className={styles.formItem}>
        {getFieldDecorator('favourite', { valuePropName: 'checked' })(<Switch size="small" />)}
        <span>Only Show Favorites</span>
      </label>

      <Row className={styles.formRow} type="flex" justify="space-between">
        <Col span={11}>
          <Form.Item label="Created Date">
            {getFieldDecorator('createdStart')(
              <DatePicker
                name="createdStart"
                placeholder="created"
                format={UserContext.getDateFormat()}
                disabledDate={disabledStartDate}
              />
            )}
          </Form.Item>
        </Col>
        <Col span={11}>
          <Form.Item label="through">
            {getFieldDecorator('createdEnd')(
              <DatePicker
                name="createdEnd"
                placeholder="through"
                format={UserContext.getDateFormat()}
                disabledDate={disabledEndDate}
              />
            )}
          </Form.Item>
        </Col>
      </Row>
      <Row className={styles.formRow}>
        <Col span={24}>
          <Form.Item label="Created By">
            {getFieldDecorator('createdBy', { initialValue: user?.userName })(
              <Select>
                {segmentUsers.length > 1 && (
                  <Option key={SHOW_ALL} value={SHOW_ALL}>
                    Show All
                  </Option>
                )}
                {segmentUsers.map((item: string) => (
                  <Option key={item} value={item}>
                    {item}
                  </Option>
                ))}
              </Select>
            )}
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
};

export default Form.create<FiltersFormProps>({
  // debounce is required here since we reset manually `createdBy` and this causes an additional call
  onValuesChange: debounce(handleFormValuesChange, 100),
})(FiltersForm);
